/**
 * @author      carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright   Copyright (c) 2025, Zumper
 * @description getReduxFriendlyFilters
 */

import { PetType, PropertyFeatures, type PropertyType } from '@zcode/enums'
import {
  APARTMENT_TYPE_GROUP,
  CONDO_TYPE_GROUP,
  HOUSE_TYPE_GROUP,
  LONG_TERM_OTHER_TYPE_GROUP,
  ROOM_TYPE_GROUP,
  TOWNHOUSE_TYPE_GROUP,
} from '@zcode/nr-utils'
import type {
  AllowedBathroomCounts,
  Filters,
  OldAIAssistantFiltersResponse,
} from '@zcode/state-type'
import type { WritableDeep } from 'type-fest'

const orFeaturesValues = [
  PropertyFeatures.SPECIALS,
  PropertyFeatures.SWEET_DEAL,
  PropertyFeatures.UTILITIES_INCLUDED,
  PropertyFeatures.NO_SECURITY_DEPOSIT,
  PropertyFeatures.PRICE_DROP,
] as const

/**
 * Maps filters returned from inventory search to redux
 */

export function getReduxFriendlyFilters(filters: OldAIAssistantFiltersResponse) {
  const reduxFilters: WritableDeep<Partial<Filters>> = {}

  if (filters?.bathrooms) {
    const minBathrooms = filters.bathrooms.min as AllowedBathroomCounts
    const maxBathrooms = filters.bathrooms.max

    if (minBathrooms && maxBathrooms && minBathrooms !== maxBathrooms) {
      reduxFilters.bathroomsRange = [minBathrooms, maxBathrooms]
    } else if (minBathrooms) {
      reduxFilters.bathroomsRange = [minBathrooms]
    } else {
      reduxFilters.bathroomsRange = []
    }
  }

  if (filters?.bedrooms) {
    const bedroomsArray: [boolean, boolean, boolean, boolean, boolean, boolean] = [
      false,
      false,
      false,
      false,
      false,
      false,
    ]

    const min = filters.bedrooms.min ?? 0
    const max = filters.bedrooms.max ?? 5

    for (let i = min; i <= Math.min(max, 5); i++) {
      bedroomsArray[i] = true
    }

    reduxFilters.bedrooms = bedroomsArray
  }

  if (filters?.price) {
    if (filters.price.min) {
      reduxFilters.minPrice = filters.price.min
    }
    if (filters.price.max) {
      reduxFilters.maxPrice = filters.price.max
    }
  }

  if (filters?.squareFeet) {
    reduxFilters.minSquareFeet = filters.squareFeet.min ?? null
    reduxFilters.maxSquareFeet = filters.squareFeet.max ?? null
  }

  if (filters?.pricePerBedroom) {
    if (filters.pricePerBedroom.min === 'p75') {
      reduxFilters.minPricePerBedroomPercentile = filters.pricePerBedroom.min
    }
    if (filters.pricePerBedroom.max === 'p50') {
      reduxFilters.maxPricePerBedroomPercentile = filters.pricePerBedroom.max
    } else if (typeof filters.pricePerBedroom.max === 'number') {
      reduxFilters.maxPricePerBedroom = `${filters.pricePerBedroom.max}`
    }
  }

  if (filters?.pets) {
    if (
      filters.pets?.include?.includes(PetType.CAT) ||
      filters.pets?.includeAll?.includes(PetType.CAT)
    ) {
      reduxFilters.cats = true
    }
    if (
      filters.pets?.include?.includes(PetType.SMALL_DOG) ||
      filters.pets?.includeAll?.includes(PetType.SMALL_DOG) ||
      filters.pets?.include?.includes(PetType.LARGE_DOG) ||
      filters.pets?.includeAll?.includes(PetType.LARGE_DOG)
    ) {
      reduxFilters.dogs = true
    }
  }

  if (filters.propertyTypes) {
    const propTypeArr = filters.propertyTypes.include ?? []
    const propertyTypeVals: PropertyType[] = []
    for (const type of propTypeArr) {
      if (APARTMENT_TYPE_GROUP.includes(type)) {
        for (const item of APARTMENT_TYPE_GROUP) {
          propertyTypeVals.push(item)
        }
      }
      if (HOUSE_TYPE_GROUP.includes(type)) {
        for (const item of HOUSE_TYPE_GROUP) {
          propertyTypeVals.push(item)
        }
      }
      if (CONDO_TYPE_GROUP.includes(type)) {
        for (const item of CONDO_TYPE_GROUP) {
          propertyTypeVals.push(item)
        }
      }
      if (ROOM_TYPE_GROUP.includes(type)) {
        for (const item of ROOM_TYPE_GROUP) {
          propertyTypeVals.push(item)
        }
      }
      if (TOWNHOUSE_TYPE_GROUP.includes(type)) {
        for (const item of TOWNHOUSE_TYPE_GROUP) {
          propertyTypeVals.push(item)
        }
      }
      if (LONG_TERM_OTHER_TYPE_GROUP.includes(type)) {
        for (const item of LONG_TERM_OTHER_TYPE_GROUP) {
          propertyTypeVals.push(item)
        }
      }
    }
    reduxFilters.propertyTypes = [...new Set(propertyTypeVals)]
  }

  const buildingInclude = filters.buildingAmenities?.include ?? []
  const buildingIncludeAll = filters.buildingAmenities?.includeAll?.flat() ?? []
  reduxFilters.buildingAmenities = [...new Set([...buildingInclude, ...buildingIncludeAll])]

  const listingInclude = filters.listingAmenities?.include ?? []
  const listingIncludeAll = filters.listingAmenities?.includeAll?.flat() ?? []
  reduxFilters.listingAmenities = [...new Set([...listingInclude, ...listingIncludeAll])]

  if (filters?.features) {
    // virtual tours
    if (
      filters.features.includeAll?.some(
        (group) =>
          Array.isArray(group) &&
          group.length === 2 &&
          group.includes(PropertyFeatures.HAS_VIDEO_MEDIA) &&
          group.includes(PropertyFeatures.HAS_IFRAME_MEDIA)
      )
    ) {
      reduxFilters.hasVirtualTours = true
    }

    // regular features
    const featuresInclude = filters.features.include ?? []
    const featuresIncludeAll = filters.features.includeAll ?? []
    const allFeatures = [...featuresInclude, ...featuresIncludeAll.flat()]
    const mergedFeatures = allFeatures.filter(
      (feat) =>
        !orFeaturesValues.includes(feat) &&
        feat !== PropertyFeatures.HAS_IFRAME_MEDIA &&
        feat !== PropertyFeatures.HAS_VIDEO_MEDIA
    )

    reduxFilters.features = [...new Set(mergedFeatures)]

    // or features
    if (allFeatures.length) {
      const orFeatures: PropertyFeatures[] = []

      for (const featGroup of allFeatures) {
        if (orFeaturesValues.includes(featGroup)) {
          orFeatures.push(featGroup)
        }
      }

      reduxFilters.orFeatures = [orFeatures]
    }
  }

  if (filters?.dateAvailable) {
    reduxFilters.availableAfter = filters.dateAvailable.min ?? null
    reduxFilters.availableBefore = filters.dateAvailable.max ?? null
  }

  if (filters?.leasingFee?.max === 0) {
    reduxFilters.noFees = true
  }

  if (filters.listedOn?.min) {
    reduxFilters.maxDays = 1
  }

  if (filters?.hasImages) {
    reduxFilters.hasImages = true
  }

  if (filters?.url) {
    reduxFilters.url = filters.url
  }

  return reduxFilters
}
